import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Pill } from "../components/Bits";
import {
    PlayIcon, PauseIcon, ArrowLeftIcon, CalendarPlusIcon, EnvelopeSimpleIcon, PhoneCallIcon, FlagIcon, InfoIcon, DownloadSimpleIcon, WarningIcon,
} from "@phosphor-icons/react";
import { OUTCOME_COLORS, OUTCOME_LABELS, SENTIMENT_COLORS, formatDuration, formatDate } from "../lib/format";
import { toast } from "sonner";

export default function CallDetail() {
    const { id } = useParams();
    const [call, setCall] = useState(null);
    const navigate = useNavigate();
    const [playing, setPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const audioRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            const { data } = await api.get(`/calls/${id}`);
            setCall(data);
        };
        load();
    }, [id]);

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) { el.pause(); } else { el.play().catch((e) => toast.error("Cannot play recording: " + e.message)); }
    };

    const flagDnc = async () => {
        if (!call) return;
        if (!window.confirm("Add this prospect to your DNC list?")) return;
        await api.post("/dnc", { phone: call.prospect_name, reason: "Flagged from call detail" });
        toast.success("Added to DNC");
    };

    if (!call) return <div className="p-8 text-[13px] text-[var(--text-secondary)]">Loading…</div>;

    const total = Math.max(call.duration_sec, 1);
    const recordingUrl = call.recording_url || "";
    const isMockRecording = !recordingUrl || recordingUrl.startsWith("mock://");
    const isRealRecording = recordingUrl.startsWith("http://") || recordingUrl.startsWith("https://");

    return (
        <div className="pb-12">
            <PageHeader
                overline="CALL DETAIL"
                title={call.prospect_name}
                description={`${call.prospect_company || "—"} · ${formatDate(call.call_timestamp)}`}
                testid="call-detail-header"
                actions={
                    <>
                        <button type="button" onClick={() => navigate(-1)} className="btn-ghost px-3 py-2 text-[13px] flex items-center gap-1.5" data-testid="call-back-button">
                            <ArrowLeftIcon size={14} weight="bold" /> Back
                        </button>
                        <Pill color={OUTCOME_COLORS[call.outcome]}>{OUTCOME_LABELS[call.outcome] || call.outcome}</Pill>
                    </>
                }
            />

            {call.vapi_error && (
                <div className="mx-8 mt-4 px-4 py-3 border border-[var(--live)] bg-[var(--live)]/5 text-[13px]" data-testid="call-error-banner">
                    <div className="overline text-[10px] text-[var(--live)] mb-1">CALL FAILED</div>
                    <div className="text-[var(--text-primary)]">{call.vapi_error}</div>
                </div>
            )}

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Player & transcript */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="swiss-section p-5" data-testid="call-player">
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={togglePlay}
                                disabled={!isRealRecording}
                                className="w-12 h-12 bg-[var(--primary)] hover:bg-[var(--accent)] text-white flex items-center justify-center rounded-[2px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                                data-testid="play-pause-button"
                                aria-label={playing ? "Pause" : "Play"}
                                title={isRealRecording ? (playing ? "Pause" : "Play recording") : "No recording available"}
                            >
                                {playing ? <PauseIcon size={18} weight="fill" /> : <PlayIcon size={18} weight="fill" />}
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="overline mb-1 flex items-center gap-2">
                                    RECORDING
                                    {isMockRecording ? (
                                        <span className="px-1.5 py-0.5 border border-[var(--warning)] text-[var(--warning)] text-[9px]" title="No real recording: this call was generated in MOCK mode (no Vapi telephony).">MOCK</span>
                                    ) : (
                                        <span className="px-1.5 py-0.5 border border-[var(--success)] text-[var(--success)] text-[9px]" title="Real Vapi call recording.">VAPI</span>
                                    )}
                                </div>
                                <div className="h-1.5 bg-[var(--surface-active)] rounded-[2px] relative overflow-hidden" data-testid="call-progress">
                                    <div
                                        className="absolute top-0 left-0 h-full bg-[var(--primary)] transition-[width] duration-300"
                                        style={{ width: `${(position / total) * 100}%` }}
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-1.5 mono text-[11px] text-[var(--text-secondary)]">
                                    <span>{formatDuration(position)}</span>
                                    <span>{formatDuration(total)}</span>
                                </div>
                                {isRealRecording && (
                                    <audio
                                        ref={audioRef}
                                        src={recordingUrl}
                                        preload="metadata"
                                        onPlay={() => setPlaying(true)}
                                        onPause={() => setPlaying(false)}
                                        onEnded={() => setPlaying(false)}
                                        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                                        onError={() => toast.error("Couldn't load recording from Vapi storage")}
                                        controls
                                        className="w-full mt-3"
                                        data-testid="call-audio-element"
                                    />
                                )}
                                {isMockRecording && (
                                    <div className="mt-2 flex items-start gap-2 text-[11px] text-[var(--text-secondary)] bg-[var(--surface-active)] px-2 py-1.5" data-testid="mock-recording-note">
                                        <WarningIcon size={12} weight="bold" className="text-[var(--warning)] shrink-0 mt-0.5" />
                                        <span>
                                            No recording available. This call was generated in MOCK mode — the transcript and sentiment are AI-simulated, no real phone call was placed. Select a Vapi phone number in Settings to enable real calls.
                                        </span>
                                    </div>
                                )}
                                {isRealRecording && (
                                    <a
                                        href={recordingUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 mt-2 text-[11px] mono text-[var(--text-secondary)] hover:text-[var(--accent)] underline"
                                        data-testid="download-recording"
                                    >
                                        <DownloadSimpleIcon size={11} weight="bold" /> Download .wav
                                    </a>
                                )}
                            </div>
                            <div className="text-right shrink-0">
                                <div className="overline flex items-center gap-1 justify-end" title="How sentiment is scored: after the call ends we send the full transcript to Replicate's Llama-3-70B model with a classification prompt. It returns label (positive/neutral/negative) and score (-1 to +1). Threshold: <-0.15 negative, -0.15..+0.15 neutral, >+0.15 positive.">
                                    <InfoIcon size={10} weight="bold" /> SENTIMENT
                                </div>
                                <div className="mono text-lg font-semibold" style={{ color: SENTIMENT_COLORS[call.sentiment_label] }} data-testid="sentiment-score">
                                    {call.sentiment_label} · {call.sentiment_score}
                                </div>
                                <div className="overline text-[9px] mt-1 text-[var(--text-secondary)]">RANGE −1 .. +1</div>
                            </div>
                        </div>
                    </div>

                    <div className="swiss-section" data-testid="call-transcript">
                        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
                            <div>
                                <div className="overline">TRANSCRIPT</div>
                                <h3 className="text-lg font-semibold tracking-tight">{call.transcript?.length || 0} turns</h3>
                            </div>
                        </div>
                        <ul className="grid-borders">
                            {(call.transcript || []).map((t, i) => {
                                const active = position >= t.timestamp_sec && (i === call.transcript.length - 1 || position < (call.transcript[i + 1]?.timestamp_sec || total));
                                return (
                                    <li
                                        key={i}
                                        className={`px-5 py-3 transition-colors ${active ? "bg-[#FEF7CD]" : ""}`}
                                        data-testid={`transcript-turn-${i}`}
                                    >
                                        <div className="flex items-baseline gap-3">
                                            <span className={`overline ${t.speaker === "agent" ? "text-[var(--accent)]" : "text-[var(--text-primary)]"}`}>
                                                {t.speaker === "agent" ? "AGENT" : "PROSPECT"}
                                            </span>
                                            <span className="mono text-[11px] text-[var(--text-secondary)]">
                                                {formatDuration(Math.round(t.timestamp_sec))}
                                            </span>
                                            {t.sentiment && (
                                                <span className="mono text-[10px] uppercase" style={{ color: SENTIMENT_COLORS[t.sentiment] }}>
                                                    · {t.sentiment}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[14px] leading-relaxed mt-1">{t.text}</p>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-3">
                    <div className="swiss-section p-5" data-testid="call-summary">
                        <div className="overline">AI SUMMARY</div>
                        <h3 className="text-lg font-semibold tracking-tight">What happened</h3>
                        <p className="text-[14px] leading-relaxed mt-2 text-[var(--text-primary)]">{call.summary || "—"}</p>
                    </div>

                    <div className="swiss-section p-5" data-testid="call-next-action">
                        <div className="overline">NEXT ACTION</div>
                        <h3 className="text-lg font-semibold tracking-tight">Recommended step</h3>
                        <p className="text-[14px] leading-relaxed mt-2">{call.next_action || "—"}</p>
                        <div className="mt-4 flex flex-col gap-2">
                            <button type="button" className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-2 justify-start" data-testid="action-send-whitepaper">
                                <EnvelopeSimpleIcon size={13} weight="bold" /> Send whitepaper email
                            </button>
                            <button type="button" className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-2 justify-start" data-testid="action-book-meeting">
                                <CalendarPlusIcon size={13} weight="bold" /> Book calendar invite
                            </button>
                            <button type="button" className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-2 justify-start" data-testid="action-callback">
                                <PhoneCallIcon size={13} weight="bold" /> Schedule callback
                            </button>
                            <button type="button" onClick={flagDnc} className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-2 justify-start" data-testid="action-flag-dnc">
                                <FlagIcon size={13} weight="bold" /> Flag as DNC
                            </button>
                        </div>
                    </div>

                    <div className="swiss-section p-5" data-testid="call-meta">
                        <div className="overline mb-2">METADATA</div>
                        <ul className="space-y-1.5 text-[12px] mono text-[var(--text-secondary)]">
                            <li>ID: <span className="text-[var(--text-primary)]">{call.id.slice(0, 8)}…</span></li>
                            <li>Campaign: <Link to={`/campaigns/${call.campaign_id}`} className="text-[var(--text-primary)] underline">{call.campaign_id.slice(0, 8)}…</Link></li>
                            <li>Recording: <span className="text-[var(--text-primary)]">{call.recording_url}</span></li>
                            <li>Duration: <span className="text-[var(--text-primary)]">{formatDuration(call.duration_sec)}</span></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
